import { useState, useEffect, useCallback } from 'react';
import { Box, Modal } from '@mui/material';
import { IoCloseCircle } from 'react-icons/io5';
import { FaPlus } from 'react-icons/fa';
import IndustryTypeTable from '../../Component/IndustryTypeTable';
import CustomSnackbar from "../../Component/CustomSnackBar";
import IndustryTypeForm from '../../Component/IndustryTypeForm';
import api from '../../Services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { FiFolder } from 'react-icons/fi'; // Icon for empty state
import ConfirmDeleteModal from '../../Component/ConfirmDelete';

export default function IndustryTypeManagement() {
  const [industryTypes, setIndustryTypes] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedIndustryTypeToEdit, setSelectedIndustryTypeToEdit] = useState(null)
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [selectededIndustryTypeToDelete, setSelectedIndustryTypeToDelete] = useState('')
  const { setIsLoading } = useLoader();

  useEffect(() => {
    const handleAllStates = async () => {
      const response = await api.minimumWages.getMinimumWagesAllStates()
      if (response.status === 200) {
        const sortedStates = [...response.data.states].sort((a, b) => a.State.localeCompare(b.State));
        const state = sortedStates[0];
        setSelectedState(state);
        setAllStates(response.data.states)
      } else {
        setAllStates([])
      }
    }
    handleAllStates()
  }, [setIsLoading]);

  const handleGetIndustryTypesBasedOnState = useCallback(async () => {
    if (!selectedState) return;
    const response = await api.minimumWages.onGetIndustryTypesBasedOnState(selectedState.StateId);
    if (response?.status === 200) {
      setIndustryTypes(response.data.data);
    } else {
      setIndustryTypes([]);
    }
  }, [selectedState]);

  useEffect(() => {
    handleGetIndustryTypesBasedOnState()
  }, [handleGetIndustryTypesBasedOnState])

  const handleEditIndustryType = async (data) => {
    setSelectedIndustryTypeToEdit(data)
    setOpenModal(true)
  };

  const handleOnEditFormSubmit = async (data) => {
    const { industryType } = data
    if (selectedIndustryTypeToEdit && selectedState) {
      setIsLoading(true)
      const response = await api.minimumWages.onEditIndustryTypeApi(selectedIndustryTypeToEdit.id, { industryType })
      if (response.status === 200) {
        handleGetIndustryTypesBasedOnState()
        setSnack({ open: true, message: 'Industry type updated successfully', severity: "success" })
        setOpenModal(false)
        setSelectedIndustryTypeToEdit(null)
      } else {
        setSnack({ open: true, message: response?.message || 'Failed to update industry type', severity: "error" })
      }
      setIsLoading(false)
    }
  }

  const handleOnDeleteIndustryType = (id) => {
    setSelectedIndustryTypeToDelete(id)
  }

  const handleOnConfrimDelete = async () => {
    if (selectededIndustryTypeToDelete) {
      setIsLoading(true)
      const response = await api.minimumWages.onDeleteIndutryTypeApi(selectededIndustryTypeToDelete)
      if (response.status === 200) {
        handleGetIndustryTypesBasedOnState()
        setSnack({ open: true, message: 'Industry type deleted successfully', severity: "success" })
        setSelectedIndustryTypeToDelete('')
      } else {
        setSnack({ open: true, message: 'Failed to delete industry type', severity: "error" })
      }
      setIsLoading(false)
    }
  };

  const handleSave = async (data) => {
    if (allStates.length) {
      const selectedState = allStates.find(val => val.StateId === data.StateId);
      setIsLoading(true)
      const response = await api.minimumWages.onCreateIndustryType({ ...data, State: selectedState?.State })
      if (response.status === 200) {
        setSelectedState({ ...selectedState })
        setSnack({ open: true, message: 'Industry type created successfully', severity: "success" })
        setOpenModal(false)
      } else {
        setSnack({ open: true, message:response.data?.message || 'Failed to create industry type', severity: "error" })
      }
      setIsLoading(false)
    }
  };

  const handleOnSelectState = (event) => {
    const state = allStates.find(val => val.StateId === event.target.value)
    setSelectedState(state)
  };

  const handleModalClose = () => {
    if (selectedIndustryTypeToEdit) {
      setSelectedIndustryTypeToEdit(null)
    }
    setOpenModal(false);
  };

  const handleOnCloseDeleteModal = () => {
    setSelectedIndustryTypeToDelete('')
  }

  return (
    <>
      <CustomSnackbar
        open={snack.open}
        handleClose={() => setSnack({ ...snack, open: false })} message={snack.message} severity={snack.severity}
      />
      <div className="m-4">
        <h1 className='text-center text-2xl font-bold mb-5'>Industry Types</h1>
        <div className="flex justify-between items-center">
           <h1 className="text-xl">{selectedState ? selectedState.State : ""}</h1> 
          <div className="flex  gap-4 items-center">
           
            <FormControl className='flex-1' fullWidth size="medium">
              <InputLabel id="state-select-label">Select State</InputLabel>
              <Select
                labelId="state-select-label"
                value={selectedState ? selectedState.StateId : ''}
                label="Select State"
                size="small"
                onChange={handleOnSelectState}
              >
                {[...allStates]
                  .sort((a, b) => a.State.localeCompare(b.State))
                  .map((state) => (
                    <MenuItem key={state.StateId} value={state.StateId}>
                      {state.State}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
             <button
              onClick={() => setOpenModal(true)}
              className="flex items-center gap-2 bg-primary-color text-white px-4 py-2 rounded whitespace-nowrap"
            >
              <FaPlus /> Create Industry Type
            </button>
          </div>
        </div>
        {industryTypes.length > 0 ? (
          <IndustryTypeTable data={industryTypes} onEdit={handleEditIndustryType} onDelete={handleOnDeleteIndustryType} />
        ) : (
          <div className="text-center text-gray-600 mt-8 p-6 border border-dashed border-gray-300 rounded-md">
            <FiFolder className="text-5xl mx-auto mb-4 text-primary-color" />
            <p className="text-lg">No industry types found.</p>
          </div>

        )}
        <Modal
          open={openModal}
          onClose={(_event, reason) => {
            if (reason !== 'backdropClick') {
              handleModalClose();
            }
          }}
        >
          <Box className="bg-white w-1/2 h-[70vh] p-6 rounded-md shadow-lg m-auto overflow-y-auto mt-4">
            <div className="flex justify-end">
              <IoCloseCircle
                className="text-2xl cursor-pointer text-gray-500 hover:text-red-500"
                onClick={handleModalClose}
              />
            </div>
            <IndustryTypeForm
              selectedState={selectedState}
              onEditFormSubmit={handleOnEditFormSubmit}
              data={selectedIndustryTypeToEdit}
              states={allStates}
              onSave={handleSave}
            />
          </Box>
        </Modal>
      </div>
      <ConfirmDeleteModal
        message='Are sure you want to delete this industry type?'
        onClose={handleOnCloseDeleteModal}
        open={!!selectededIndustryTypeToDelete}
        onConfirm={handleOnConfrimDelete}
      />
    </>
  );
}
